/**
 * One block of the page in website Make changes (docs/website_make_changes.md):
 * drawn by the real runtime, wrapped through SmartPageRuntime's
 * BlockChromeContext seam with
 *  - a toolbar on hover — its name, ↑ ↓, ⚙, Copy, Delete, + Add a block;
 *  - typing on the page — a click on words the block holds (contentFields.json
 *    text fields, button words, list points) makes just those words editable
 *    in place; Enter or a click away stores them, Esc drops them;
 *  - a click anywhere else on the block opens its ⚙ panel.
 * While editing, links and buttons inside the page never navigate.
 */

import { Fragment, useEffect, useRef, useState, type ReactNode } from 'react'
import { ArrowDown, ArrowUp, Copy, Plus, Settings, Trash2 } from 'lucide-react'
import type { Block } from '@/pages/designers/page/types'
import { plainWords, textTargets, type TextTarget } from './pageOps'

export interface BlockChromeProps {
  block: Block
  label: string
  selected: boolean
  canUp: boolean
  canDown: boolean
  /** Open the block's ⚙ panel next to `anchor` (its toolbar). */
  onOpen: (anchor: HTMLElement) => void
  /** Store typed words; resolves once stored (or refused). */
  onType: (target: TextTarget, text: string) => Promise<void>
  /** Typing started (an open panel closes). */
  onTypingStart: () => void
  onMove: (dir: -1 | 1) => void
  onCopy: () => void
  onDelete: () => void
  onAdd: () => void
  children: ReactNode
}

interface Typing { el: HTMLElement; target: TextTarget; off: () => void }

/** The innermost elements under `root` showing exactly `words`, in page order. */
function elementsShowing(root: HTMLElement, words: string): HTMLElement[] {
  const all = Array.from(root.querySelectorAll<HTMLElement>('*'))
    .filter((el) => plainWords(el.textContent || '') === words)
  return all.filter((el) => !all.some((o) => o !== el && el.contains(o)))
}

export function EditableBlock(p: BlockChromeProps) {
  const ref = useRef<HTMLDivElement>(null)
  // A new number remounts the block: after typing, the DOM the browser
  // changed is no longer React's, so it is drawn afresh from the model.
  const [rev, setRev] = useState(0)
  const [typing, setTyping] = useState(false)
  const typingRef = useRef<Typing | null>(null)
  const props = useRef(p)
  props.current = p

  const finish = (commit: boolean) => {
    const cur = typingRef.current
    if (!cur) return
    typingRef.current = null
    cur.off()
    const raw = cur.target.kind === 'long' ? cur.el.innerText : (cur.el.textContent || '')
    const text = cur.target.kind === 'long' ? raw.replace(/ /g, ' ').trim() : plainWords(raw)
    cur.el.removeAttribute('contenteditable')
    setTyping(false)
    const changed = commit && plainWords(text) !== plainWords(cur.target.value)
    if (!changed) { setRev((n) => n + 1); return }
    props.current.onType(cur.target, text)
      .catch((e) => console.warn('[site-edit] typed words were not stored', e))
      .finally(() => setRev((n) => n + 1))
  }
  // Leaving the mode (or the block going away) mid-typing keeps the words.
  useEffect(() => () => { if (typingRef.current) finish(true) }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const startTyping = (el: HTMLElement, target: TextTarget, x: number, y: number) => {
    if (typingRef.current) finish(true)
    props.current.onTypingStart()
    el.setAttribute('contenteditable', 'plaintext-only')
    if (el.contentEditable !== 'plaintext-only') el.setAttribute('contenteditable', 'true')
    el.setAttribute('data-tw-field', 'site-edit-typing')
    el.focus()
    try {
      const range = (document as Document & { caretRangeFromPoint?: (x: number, y: number) => Range | null })
        .caretRangeFromPoint?.(x, y)
      if (range && el.contains(range.startContainer)) {
        const sel = window.getSelection()
        sel?.removeAllRanges()
        sel?.addRange(range)
      }
    } catch { /* the caret just lands at the start */ }
    const onKey = (e: KeyboardEvent) => {
      e.stopPropagation()
      if (e.key === 'Escape') { e.preventDefault(); finish(false); return }
      if (e.key === 'Enter' && (target.kind === 'text' || !e.shiftKey)) { e.preventDefault(); finish(true) }
    }
    const onBlur = () => finish(true)
    el.addEventListener('keydown', onKey)
    el.addEventListener('blur', onBlur)
    typingRef.current = {
      el, target,
      off: () => { el.removeEventListener('keydown', onKey); el.removeEventListener('blur', onBlur) },
    }
    setTyping(true)
  }

  // The panel sits beside the block's toolbar, not the whole block: a block
  // as wide as the page would push it over the very words being changed.
  const open = () => {
    const root = ref.current
    if (root) p.onOpen(root.querySelector<HTMLElement>(':scope > .swe-bctl') || root)
  }

  const onClickCapture = (e: React.MouseEvent<HTMLDivElement>) => {
    const root = ref.current
    const t = e.target as HTMLElement
    if (!root || !(t instanceof HTMLElement)) return
    if (t.closest('.swe-bctl')) return                         // its own toolbar
    if (t.closest('[data-swe-block]') !== root) return         // an inner block decides
    // Links and buttons inside the page stay where they are while editing.
    e.preventDefault()
    e.stopPropagation()
    const cur = typingRef.current
    if (cur && cur.el.contains(t)) return                      // moving the caret
    const targets = textTargets(p.block)
    for (let el: HTMLElement | null = t; el && el !== root; el = el.parentElement) {
      const words = plainWords(el.textContent || '')
      if (!words) continue
      const same = targets.filter((x) => plainWords(x.value) === words)
      if (same.length === 0) continue
      // A <button> cannot hold a caret: its words are changed in the panel.
      if (el.tagName === 'BUTTON' || el.closest('button')) break
      // The same words twice (two cards both "A short heading"): the k-th on
      // the page is the k-th in the model.
      const k = Math.max(0, elementsShowing(root, words).indexOf(el))
      startTyping(el, same[Math.min(k, same.length - 1)], e.clientX, e.clientY)
      return
    }
    open()
  }

  return (
    <div ref={ref} className={`swe-blk${p.selected || typing ? ' swe-on' : ''}`} data-swe-block={p.block.id}
      data-tw-block={`block:${p.block.id}`} onClickCapture={onClickCapture}>
      <div className="swe-bctl" role="toolbar" aria-label={`${p.label} block`}>
        <span className="swe-bctl-name">{p.label}</span>
        <Tool label="Move up" hook="block-up" disabled={!p.canUp} onClick={() => p.onMove(-1)}><ArrowUp size={13} /></Tool>
        <Tool label="Move down" hook="block-down" disabled={!p.canDown} onClick={() => p.onMove(1)}><ArrowDown size={13} /></Tool>
        <Tool label={`${p.label} settings`} hook="block-settings" onClick={open}><Settings size={13} /></Tool>
        <Tool label="Copy" hook="block-copy" onClick={p.onCopy}><Copy size={13} /></Tool>
        <Tool label="Delete" hook="block-delete" danger onClick={p.onDelete}><Trash2 size={13} /></Tool>
        <Tool label="Add a block after this one" hook="block-add" onClick={p.onAdd}><Plus size={13} /></Tool>
      </div>
      <Fragment key={rev}>{p.children}</Fragment>
    </div>
  )
}

function Tool({ label, hook, onClick, disabled, danger, children }: {
  label: string; hook: string; onClick: () => void; disabled?: boolean; danger?: boolean; children: ReactNode
}) {
  return (
    <button type="button" aria-label={label} title={label} disabled={disabled}
      data-tw-action={`site-edit-${hook}`}
      onMouseDown={(e) => e.preventDefault()}   // keep the words being typed focused until the click
      onClick={(e) => { e.stopPropagation(); e.preventDefault(); if (!disabled) onClick() }}
      className={danger ? 'swe-tool swe-tool-danger' : 'swe-tool'}>{children}</button>
  )
}

// The block chrome's look — the mode's violet, scoped to `.swe-blk` / `.swe-bctl`.
// The toolbar shows for the INNERMOST block under the pointer only.
export const BLOCK_CSS = `
.swe-blk { position: relative; border-radius: 4px; }
.swe-blk:hover:not(:has(.swe-blk:hover)) { box-shadow: 0 0 0 1px rgba(124,58,237,.55); }
.swe-blk.swe-on { box-shadow: 0 0 0 2px #7c3aed; }
.swe-bctl { position: absolute; top: 0; left: 0; transform: translateY(-100%); z-index: 45; display: none;
  align-items: center; gap: 2px; padding: 2px 3px 2px 8px; border-radius: 6px 6px 0 0; background: #7c3aed;
  font: 600 12.5px 'IBM Plex Sans', system-ui, sans-serif; color: #fff; white-space: nowrap; }
.swe-blk:hover:not(:has(.swe-blk:hover)) > .swe-bctl, .swe-blk.swe-on > .swe-bctl { display: inline-flex; }
.swe-bctl-name { padding-right: 4px; }
.swe-tool { width: 24px; height: 22px; padding: 0; border: 0; border-radius: 4px; background: transparent; color: #fff;
  display: inline-flex; align-items: center; justify-content: center; cursor: pointer; }
.swe-tool:hover:not(:disabled) { background: rgba(255,255,255,.2); }
.swe-tool:disabled { opacity: .4; cursor: default; }
.swe-tool-danger:hover:not(:disabled) { background: #b42318; }
.swe-blk [contenteditable] { outline: 2px dashed #a78bfa; outline-offset: 2px; cursor: text; }
`
